// src/features/affiliate/PayoutRequestDialog.tsx
// Partner requests a payout for their earned commissions.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliatePayout, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import type { Affiliate, PaymentMethod } from '@core/types/affiliate';
import { errMessage, formatMoney, makeEntity } from './format';

const PAYMENT_METHODS: PaymentMethod[] = [
  'PAYMENT_METHOD_BANK_TRANSFER',
  'PAYMENT_METHOD_WIRE_TRANSFER',
  'PAYMENT_METHOD_ACH',
  'PAYMENT_METHOD_SEPA',
  'PAYMENT_METHOD_DIGITAL_WALLET',
  'PAYMENT_METHOD_CRYPTO',
  'PAYMENT_METHOD_INVOICE',
  'PAYMENT_METHOD_BALANCE',
];

interface PayoutRequestDialogProps {
  open: boolean;
  onClose: () => void;
  affiliate: Affiliate;
  onRequested: () => void;
}

export const PayoutRequestDialog = observer(({ open, onClose, affiliate, onRequested }: PayoutRequestDialogProps) => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const store = useAffiliatePayout();

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>(affiliate.payout_preferences?.preferred_method ?? 'PAYMENT_METHOD_BANK_TRANSFER');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAmount('');
    setMethod(affiliate.payout_preferences?.preferred_method ?? 'PAYMENT_METHOD_BANK_TRANSFER');
  }, [open, affiliate]);

  const currency = affiliate.available_balance?.currency ?? affiliate.payout_preferences?.currency ?? 'CURRENCY_USD';

  const submit = async () => {
    const amountNum = Number(amount);
    if (!amount || Number.isNaN(amountNum) || amountNum <= 0 || !affiliate.entity?.id) return;
    setIsSaving(true);
    setError(null);
    try {
      await store.RequestPayout({
        entity: makeEntity('', 'payout', scope),
        affiliate_id: affiliate.entity.id,
        amount: { amount: Math.round(amountNum * 100), currency, decimal_places: 2 },
        payment_method: method,
        metadata: {},
      });
      onRequested();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('affiliate.payouts.requestError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('affiliate.payouts.request')}
      canSubmit={!!amount && Number(amount) > 0}
      isSubmitting={isSaving}
      dirty={!!amount}
      width="md"
      aria-label={t('affiliate.payouts.requestTitle')}
    >
      <div className="space-y-2 px-3.5 pb-3 pt-3">
        {error && <Alert variant="error">{error}</Alert>}
        <p className="text-sm text-muted-foreground">
          {t('affiliate.payouts.availableBalance', { amount: formatMoney(affiliate.available_balance, locale) })}
        </p>
        <FormRow label={t('affiliate.payouts.amount')} labelWidth="sm" htmlFor="payout-amount">
          <input
            id="payout-amount"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="0.00"
            className="w-32 rounded-md border border-input bg-background px-2.5 py-1.5 text-right text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('affiliate.profile.preferredMethod')} labelWidth="sm">
          <Select
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            options={PAYMENT_METHODS.map((v) => ({ value: v, label: t(`affiliate.paymentMethod.${v.replace('PAYMENT_METHOD_', '').toLowerCase()}`) }))}
            aria-label={t('affiliate.profile.preferredMethod')}
            fullWidth={false}
            className="w-56"
          />
        </FormRow>
      </div>
    </FormDialog>
  );
});
